/**
 * Block state machinery.
 *
 * A "block state" is a single 16-bit integer. Each block owns a contiguous
 * range of state ids covering the cartesian product of its properties, exactly
 * like vanilla's flattened state table. That makes the world array a plain
 * Uint16Array, makes state comparison a integer compare, and lets property
 * lookups be array indexing rather than object access — which matters because
 * the redstone engine reads properties millions of times per second.
 */

export interface PropDef {
  name: string;
  values: readonly string[];
  /** Multiplier for this property's value index within the block's state range. */
  stride: number;
}

/** A property declaration before strides are computed. */
export type PropSpec = readonly [name: string, values: readonly string[]];

export const boolProp = (name: string): PropSpec => [name, ['false', 'true']];
export const intProp = (name: string, min: number, max: number): PropSpec => {
  const values: string[] = [];
  for (let i = min; i <= max; i++) values.push(String(i));
  return [name, values];
};
export const enumProp = (name: string, values: readonly string[]): PropSpec => [name, values];

export const FACING6 = ['down', 'up', 'north', 'south', 'west', 'east'] as const;
export const FACING4 = ['north', 'south', 'west', 'east'] as const;

/**
 * Registry of property lookup tables. `tables.get(propName)[state]` gives the
 * value *index* for that state, or -1 when the state's block lacks the property.
 */
export class StateTables {
  readonly tables = new Map<string, Int16Array>();
  totalStates = 0;

  alloc(name: string, totalStates: number): Int16Array {
    let arr = this.tables.get(name);
    if (!arr || arr.length < totalStates) {
      const next = new Int16Array(totalStates).fill(-1);
      if (arr) next.set(arr);
      arr = next;
      this.tables.set(name, arr);
    }
    return arr;
  }

  get(name: string): Int16Array | undefined {
    return this.tables.get(name);
  }
}
